import { observable } from "@legendapp/state";
import { getCreativePerformanceReport } from "@/apis/clients/reportingClient";
import {
  createRequestGuard,
  reportScopeKey,
  toErrorMessage,
} from "@/stores/storeHelpers";
import type { PerformanceReportRow, ReportScope } from "@/types/reporting";

interface CreativePerformanceState {
  rows: PerformanceReportRow[];
  isLoading: boolean;
  error: string | null;
  loadedForScopeKey: string | null;
}

const defaultValues: CreativePerformanceState = {
  rows: [],
  isLoading: false,
  error: null,
  loadedForScopeKey: null,
};

export const CreativePerformanceStore$ = observable(defaultValues);

const getScopeKey = (scope: ReportScope) =>
  `creative-performance:${reportScopeKey(scope)}`;

const guard = createRequestGuard();

export const fetchCreativePerformance = async (scope: ReportScope) => {
  const scopeKey = getScopeKey(scope);
  const result = guard.check(
    scopeKey,
    CreativePerformanceStore$.loadedForScopeKey.get()
  );
  if (result.outcome === "cached") {
    CreativePerformanceStore$.assign({ isLoading: false, error: null });
    return;
  }
  if (result.outcome === "in-flight") return;
  const { requestId } = result;
  CreativePerformanceStore$.assign({ isLoading: true, error: null });
  try {
    const response = await getCreativePerformanceReport(scope);
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    CreativePerformanceStore$.assign({
      rows: response.results,
      loadedForScopeKey: scopeKey,
    });
  } catch (error) {
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    CreativePerformanceStore$.error.set(toErrorMessage(error));
  } finally {
    if (!guard.isStale(requestId)) {
      CreativePerformanceStore$.isLoading.set(false);
    }
  }
};

export const clearCreativePerformance = () => {
  guard.invalidate();
  CreativePerformanceStore$.assign({
    error: null,
    isLoading: false,
    loadedForScopeKey: null,
    rows: [],
  });
};
